import React from "react";
import {
  ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine, Camera, ChevronLeft,
  Flame, RefreshCw, Sliders, Trophy, Wallet
} from "lucide-react";
import { formatUSD, formatLBP, fileUrl } from "@/lib/api";
import { BaseCard, GoldButton, ProgressBar, Badge } from "@/components/ui/DesignSystem";
import { AvatarVisual } from '@/components/store/StoreVisuals';
import { assetUrl } from '@/lib/store';
import { useTheme } from '@/context/ThemeContext';

const transactionLabel = transaction => {
  if (transaction.note) return transaction.note;
  if (transaction.type === 'deposit') return 'إيداع';
  if (transaction.type === 'withdraw') return 'سحب';
  if (transaction.type === 'transfer') return 'تحويل مالي';
  return 'عملية مالية';
};

export default function HomeTab({ me, txs, onNavigate, onOpenCustomizer, onStartOperation, onEditAvatar }) {
  const { avatarEquipment, badge, title } = useTheme();
  const hasStoreAvatar = Object.keys(avatarEquipment || {}).length > 0;
  const profile = me?.financial_profile || {};
  const xp = Number(profile.xp || 0);
  const level = Number(me?.level_info?.level || profile.level || 1);
  const streak = Number(profile.streak || 0);
  const recentTxs = (txs || []).slice(0, 5);
  const plan = me?.subscription?.type;
  const planLabel = plan === 'bank' ? 'اشتراك بنك العائلة' : plan === 'bank_amwali_plus' ? 'باقة أموالي +' : 'بدون اشتراك';
  const readOnly = me?.subscription?.state !== 'active' && !me?.view_as;
  const actions = [
    { id: 'transfer', label: 'تحويل أموال', icon: ArrowLeftRight, tone: 'blue' },
    { id: 'deposit', label: 'إيداع', icon: ArrowDownToLine, tone: 'emerald' },
    { id: 'withdraw', label: 'سحب', icon: ArrowUpFromLine, tone: 'danger' },
    { id: 'exchange', label: 'تحويل عملة', icon: RefreshCw, tone: 'champagne' },
  ];

  return (
    <div className="family-user-home space-y-3 sm:space-y-4 animate-fade-in">
      {readOnly && <div className="mb-surface flex flex-wrap items-center justify-between gap-3 p-4 text-sm"><span>حسابك متاح للعرض فقط. يمكنك الاطلاع على الأرصدة والسجل حتى تفعيل اشتراك جديد.</span><button className="btn-navy px-4 py-2" onClick={() => onNavigate('subscription')}>عرض الاشتراكات</button></div>}
      <section className="fb-profile-hero">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <button type="button" onClick={onEditAvatar} className="fb-profile-avatar group" aria-label="تعديل صورة الملف الشخصي" title="تعديل صورة الملف الشخصي">
            {me?.avatar_path ? (
              <img src={fileUrl(me.avatar_path)} alt={me.display_name || me.username} className="h-full w-full object-cover" />
            ) : hasStoreAvatar ? (
              <AvatarVisual equipped={avatarEquipment} className="h-full w-full" />
            ) : (
              <span>{me?.display_name?.charAt(0) || me?.username?.charAt(0) || 'ع'}</span>
            )}
            <span className="fb-profile-avatar__edit"><Camera aria-hidden="true" /></span>
          </button>
          <div className="min-w-0">
            <p className="text-xs font-bold text-[var(--brand-champagne)]">مرحباً بعودتك</p>
            <h2 className="truncate text-xl font-black text-[var(--brand-ivory)] sm:text-2xl">{me?.display_name || me?.username}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge variant="default">{planLabel}</Badge>
              {(title || badge) && <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-[var(--brand-champagne)]">{badge && <img src={assetUrl(badge.actual_asset || badge.preview_asset)} alt={badge.name} className="h-4 w-4 object-contain" />}{title && <span>{title.configuration?.title_text || title.name}</span>}</span>}
            </div>
          </div>
        </div>
        <button type="button" onClick={onOpenCustomizer} className="fb-icon-button" title="تخصيص اللوحة" aria-label="تخصيص اللوحة"><Sliders /></button>
      </section>

      <section className="grid grid-cols-2 gap-3" aria-label="الأرصدة الحالية">
        <article className="fb-balance-card fb-balance-card--usd">
          <div className="fb-balance-card__icon"><span>$</span></div>
          <div className="min-w-0"><p>الرصيد بالدولار</p><strong className="font-num">{formatUSD(me?.balance_usd || 0)}</strong><small>USD</small></div>
        </article>
        <article className="fb-balance-card fb-balance-card--lbp">
          <div className="fb-balance-card__icon"><Wallet /></div>
          <div className="min-w-0"><p>الرصيد بالليرة اللبنانية</p><strong className="font-num">{formatLBP(me?.balance_lbp || 0)}</strong><small>LBP</small></div>
        </article>
      </section>

      <section className="grid grid-cols-2 gap-2.5 sm:grid-cols-4" aria-label="الإجراءات المالية السريعة">
        {actions.map(({ id, label, icon: Icon, tone }) => (
          <button key={id} type="button" disabled={readOnly} title={readOnly ? 'يتطلب اشتراكاً فعالاً' : label} onClick={() => onStartOperation(id)} className={`fb-quick-action fb-quick-action--${tone} disabled:cursor-not-allowed disabled:opacity-50`}>
            <span><Icon /></span><b>{label}</b>
          </button>
        ))}
      </section>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.6fr)_minmax(18rem,.8fr)]">
        <BaseCard className="fb-dashboard-card !p-3 sm:!p-4">
          <div className="fb-card-heading">
            <h3>آخر المعاملات المالية</h3>
            <button type="button" onClick={() => onNavigate('money', 'transactions')}>عرض الكل <ChevronLeft /></button>
          </div>
          {recentTxs.length === 0 ? <div className="py-8 text-center text-xs text-[var(--color-text-secondary)]">لا توجد معاملات مسجلة حتى الآن</div> : (
            <div className="divide-y divide-white/10">
              {recentTxs.map(transaction => {
                const positive = transaction.direction === 'in' || transaction.type === 'deposit' || (transaction.type === 'transfer' && transaction.counterparty_id === me?.id);
                return <div key={transaction.id} className="fb-transaction-row">
                  <span className={`fb-transaction-row__icon ${positive ? 'positive' : 'negative'}`}>{positive ? <ArrowDownToLine /> : <ArrowUpFromLine />}</span>
                  <span className="min-w-0 flex-1"><b className="block truncate">{transactionLabel(transaction)}</b><small>{transaction.created_at ? new Date(transaction.created_at).toLocaleDateString('ar-LB') : ''}</small></span>
                  <strong className={`font-num ${positive ? 'text-emerald-400' : 'text-red-400'}`}>{positive ? '+' : '-'}{transaction.currency === 'LBP' ? `${formatLBP(transaction.amount)} ل.ل` : `$${formatUSD(transaction.amount)}`}</strong>
                </div>;
              })}
            </div>
          )}
        </BaseCard>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <BaseCard className="fb-dashboard-card !p-4">
            <div className="flex items-center justify-between gap-3"><div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-[var(--brand-champagne)]" /><h3 className="font-black">تقدمك</h3></div><Badge variant="gold">المستوى {level}</Badge></div>
            <div className="mt-4 flex items-center justify-between text-xs"><span>التقدم نحو المستوى التالي</span><b className="font-num">{xp} XP</b></div>
            <ProgressBar value={xp % 1000} max={1000} color="gold" className="mt-2" />
            <div className="mt-3 flex items-center justify-between rounded-xl border border-[var(--brand-champagne)]/25 bg-[var(--brand-champagne)]/10 p-2.5 text-xs"><span className="inline-flex items-center gap-1.5"><Flame className="h-4 w-4 text-[var(--brand-champagne)]" />السلسلة اليومية</span><b className="font-num">{streak} أيام</b></div>
            <GoldButton onClick={() => onNavigate('gamification')} fullWidth size="sm" className="mt-3">مركز المكافآت</GoldButton>
          </BaseCard>

        </div>
      </div>

    </div>
  );
}
